"use client";

import {
  CaretUpDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
} from "@phosphor-icons/react";
import { Command as CommandPrimitive } from "cmdk";
import * as React from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type ComboboxOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type ComboboxProps = {
  options: ComboboxOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  /**
   * Render the trigger with the destructive border + ring. Use for
   * form-level error states — pairs with `<FieldError />` in a Field.
   */
  invalid?: boolean;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
};

function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No results.",
  disabled,
  invalid,
  className,
  triggerClassName,
  contentClassName,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            disabled={disabled}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-invalid={invalid || undefined}
            data-slot="combobox-trigger"
            className={cn(
              "flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-normal transition-colors outline-none",
              "hover:bg-accent hover:text-accent-foreground",
              "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "aria-invalid:border-destructive",
              selected ? "text-foreground" : "text-muted-foreground",
              triggerClassName,
              className,
            )}
          >
            <span className="flex-1 truncate text-left">
              {selected ? selected.label : placeholder}
            </span>
            <CaretUpDownIcon className="size-4 shrink-0 opacity-50" />
          </button>
        }
      />
      <PopoverContent
        className={cn(
          "w-(--anchor-width) gap-0 overflow-hidden p-0",
          contentClassName,
        )}
      >
        <Command className="rounded-none! p-0!">
          <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border px-3">
            <MagnifyingGlassIcon className="size-4 shrink-0 text-muted-foreground" />
            <CommandPrimitive.Input
              placeholder={searchPlaceholder}
              className="flex-1 bg-transparent text-sm font-normal outline-none placeholder:text-muted-foreground"
            />
          </div>
          <CommandList className="p-1">
            <CommandEmpty className="py-6 text-center text-sm font-normal text-muted-foreground">
              {emptyMessage}
            </CommandEmpty>
            <CommandGroup className="p-0">
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className="h-8 gap-2 rounded-sm px-2 font-normal data-selected:bg-accent! data-selected:text-accent-foreground!"
                  onSelect={(next) => {
                    onValueChange?.(next === value ? "" : next);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      "size-4",
                      value === option.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { Combobox, type ComboboxOption, type ComboboxProps };
